import * as React from "react";
import { makeStyles, tokens } from "@fluentui/react-components";
import { isAuthenticated } from "../services/auth";
import { Screen, Envelope, Organization, Recipient, PlacedField } from "../types";
import LoginScreen from "./LoginScreen";
import OrganizationSelector from "./OrganizationSelector";
import RecipientEntry from "./RecipientEntry";
import FieldPlacement from "./FieldPlacement";
import ReviewAndSend from "./ReviewAndSend";
import StatusConfirmation from "./StatusConfirmation";

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    padding: "16px",
    boxSizing: "border-box",
    fontFamily: tokens.fontFamilyBase,
  },
});

const App: React.FC = () => {
  const styles = useStyles();
  const [screen, setScreen] = React.useState<Screen>(isAuthenticated() ? "org-selector" : "login");
  const [selectedOrg, setSelectedOrg] = React.useState<Organization | null>(null);
  const [recipients, setRecipients] = React.useState<Recipient[]>([{ name: "", email: "", role: "signer" }]);
  const [signingOrder, setSigningOrder] = React.useState<"sequential" | "parallel">("sequential");
  const [placedFields, setPlacedFields] = React.useState<PlacedField[]>([]);
  const [envelope, setEnvelope] = React.useState<Envelope | null>(null);

  const handleLoginSuccess = () => {
    setScreen("org-selector");
  };

  const handleOrgSelected = (org: Organization) => {
    setSelectedOrg(org);
    setScreen("recipients");
  };

  const handleRecipientsNext = (recs: Recipient[], order: "sequential" | "parallel") => {
    setRecipients(recs);
    setSigningOrder(order);
    setPlacedFields([]);
    setScreen("field-placement");
  };

  const handleFieldsNext = () => {
    setScreen("review");
  };

  const handleSendSuccess = (env: Envelope) => {
    setEnvelope(env);
    setScreen("status");
  };

  const handleSendAnother = () => {
    setRecipients([{ name: "", email: "", role: "signer" }]);
    setPlacedFields([]);
    setEnvelope(null);
    setScreen("recipients");
  };

  const handleBack = (target: Screen) => {
    setScreen(target);
  };

  return (
    <div className={styles.root}>
      {screen === "login" && <LoginScreen onSuccess={handleLoginSuccess} />}
      {screen === "org-selector" && <OrganizationSelector onSelect={handleOrgSelected} />}
      {screen === "recipients" && (
        <RecipientEntry
          recipients={recipients}
          signingOrder={signingOrder}
          onNext={handleRecipientsNext}
          onBack={() => handleBack("org-selector")}
        />
      )}
      {screen === "field-placement" && (
        <FieldPlacement
          recipients={recipients}
          placedFields={placedFields}
          onFieldsChanged={setPlacedFields}
          onNext={handleFieldsNext}
          onBack={() => handleBack("recipients")}
        />
      )}
      {screen === "review" && selectedOrg && (
        <ReviewAndSend
          organization={selectedOrg}
          recipients={recipients}
          signingOrder={signingOrder}
          placedFields={placedFields}
          onSuccess={handleSendSuccess}
          onBack={() => handleBack("field-placement")}
        />
      )}
      {screen === "status" && envelope && selectedOrg && (
        <StatusConfirmation
          envelope={envelope}
          organizationId={selectedOrg.id}
          onSendAnother={handleSendAnother}
        />
      )}
    </div>
  );
};

export default App;
